import { z } from 'zod';

/** YouTube and Vimeo only in V1 (IMM-1165). Display names („YouTube", „Vimeo") live in i18n. */
export const VIDEO_LINK_PROVIDERS = ['YOUTUBE', 'VIMEO'] as const;

export const VideoLinkProviderSchema = z.enum(VIDEO_LINK_PROVIDERS);

export type VideoLinkProvider = z.infer<typeof VideoLinkProviderSchema>;

/** Longest link accepted, before normalisation. */
export const VIDEO_LINK_MAX_LENGTH = 2_000;

/** The provider's title is cut to this length when the video is saved (IMM-1165 `## Fields`, Titel 1–120). */
export const VIDEO_LINK_TITLE_MAX_LENGTH = 120;

export const PropertyMediaVideoLinkSchema = z.object({
  provider: VideoLinkProviderSchema,
  // YouTube: the 11-character id. Vimeo: the numeric id. Unique per Objekt and provider.
  videoId: z.string(),
  // The page to open at the provider, never embedded: the link as entered, upgraded to https, with
  // every query parameter but the id (and Vimeo's unlisted hash) and any fragment removed.
  url: z.string(),
});

export type PropertyMediaVideoLink = z.infer<typeof PropertyMediaVideoLinkSchema>;

// `link` is checked by the domain, not here: a Zod failure is a generic 400 the dialog may not
// render (ADR 0014), and the refusal must carry its own code.
export const AddPropertyMediaVideoLinkBodySchema = z
  .object({
    link: z.string(),
  })
  .strict();

export type AddPropertyMediaVideoLinkBody = z.infer<typeof AddPropertyMediaVideoLinkBodySchema>;

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;
const YOUTUBE_HOSTS = new Set(['youtube.com', 'www.youtube.com', 'm.youtube.com']);
const VIMEO_ID = /^[0-9]{1,12}$/;
const VIMEO_HASH = /^[0-9a-f]{6,32}$/;
const VIMEO_HOSTS = new Set(['vimeo.com', 'www.vimeo.com']);

function youtube(videoId: string, url: string): PropertyMediaVideoLink {
  return { provider: 'YOUTUBE', videoId, url };
}

function vimeo(videoId: string, url: string): PropertyMediaVideoLink {
  return { provider: 'VIMEO', videoId, url };
}

function recognizeYouTube(host: string, parsed: URL): PropertyMediaVideoLink | null {
  const segments = parsed.pathname.replace(/\/$/, '').split('/').slice(1);

  if (host === 'youtu.be') {
    const [id] = segments;

    return segments.length === 1 && id !== undefined && YOUTUBE_ID.test(id)
      ? youtube(id, `https://youtu.be/${id}`)
      : null;
  }

  if (!YOUTUBE_HOSTS.has(host)) return null;

  if (parsed.pathname === '/watch') {
    const id = parsed.searchParams.get('v');

    return id !== null && YOUTUBE_ID.test(id) ? youtube(id, `https://${host}/watch?v=${id}`) : null;
  }

  const [kind, id] = segments;

  if (segments.length === 2 && (kind === 'shorts' || kind === 'embed') && id !== undefined && YOUTUBE_ID.test(id)) {
    return youtube(id, `https://${host}/${kind}/${id}`);
  }

  return null;
}

function recognizeVimeo(host: string, parsed: URL): PropertyMediaVideoLink | null {
  const segments = parsed.pathname.replace(/\/$/, '').split('/').slice(1);

  if (VIMEO_HOSTS.has(host)) {
    const [id, hash] = segments;

    if (id === undefined || !VIMEO_ID.test(id) || segments.length > 2) return null;

    if (hash === undefined) return vimeo(id, `https://${host}/${id}`);

    return VIMEO_HASH.test(hash) ? vimeo(id, `https://${host}/${id}/${hash}`) : null;
  }

  if (host === 'player.vimeo.com') {
    const [kind, id] = segments;

    if (segments.length !== 2 || kind !== 'video' || id === undefined || !VIMEO_ID.test(id)) return null;

    const hash = parsed.searchParams.get('h');
    const base = `https://player.vimeo.com/video/${id}`;

    return vimeo(id, hash !== null && VIMEO_HASH.test(hash) ? `${base}?h=${hash}` : base);
  }

  return null;
}

/**
 * Recognise a YouTube or Vimeo video page (IMM-1165 Detailed Business Rule 1). Pure and
 * side-effect-free: the server's `parseVideoLink` is authoritative, and the dialog uses the same
 * function for its inline check. Null for anything else.
 */
export function recognizeVideoLink(raw: string): PropertyMediaVideoLink | null {
  const trimmed = raw.trim();

  if (trimmed.length > VIDEO_LINK_MAX_LENGTH || !URL.canParse(trimmed)) return null;

  const parsed = new URL(trimmed);

  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;

  const host = parsed.hostname.toLowerCase();

  return recognizeYouTube(host, parsed) ?? recognizeVimeo(host, parsed);
}
